import React, { useEffect, useState } from 'react';
import { bool, func, object } from 'prop-types';

import * as styles from './styles/styles.module.scss';

import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { isAnotherMemberView, userCanEdit } from '../../../utils/user_helpers';

import TrackDetailsCard from '../../cards/TrackDetailsCard';
import LinkedCard from '../LinkedCard';
import PhotosSection from '../Photos/PhotosSection';
import SplitsTab from '../Splits/SplitsTab';
import StatsTab from '../Stats/StatsTab';
import Accordion from '../../shared/Accordion';
import DescriptionSection from '../DescriptionSection';
import WaypointsWrapper from '../../Waypoints/WaypointsWrapper';

const TrackPanel = ({
  context,
  data,
  isMobileWidth,
  handlePhotosUpload,
  handleMapInfoChanged,
  messagingChannel,
  result,
  openTrailFormModal,
  resultCardFunctions,
  listMethods,
  handleTrackUpdate,
  handleShareClick,
  saveMapAsClone,
  blockNonUserCreation,
  currentPage,
  showMapMobile,
  trackTrail
}) => {
  const [hidden, setIsHidden] = useState(null);
  const [hideStatusText, setHideStatusText] = useState(null);
  const [accordionStates, setAccordionStates] = useState({
    stats: true,
    splits: true,
    photos: true,
    waypoints: true,
    description: true
  });
  const [descriptionValue, setDescriptionValue] = useState('');
  const [isEditShown, setIsEditShown] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  useEffect(() => {
    // set the description to editing state when the description does not yet exist and if
    // the user is viewing their own activity or an admin user is viewing any activity
    if (
      ((context?.currentUser && (data?.user?.id === context?.currentUser?.id || context?.adminUser)) ||
        data?.user == null ||
        data?.user?.username == StringConstantsMixin.constants.MAGIC.ANONYMOUS_MAP_USERNAME) &&
      !data?.description
    ) {
      setIsEditShown(true);
    }
    // set hide/unhide linked trail option for an admin users
    if (context.adminUser) {
      setIsHidden(data.hidden);
      if (data.hidden) {
        setHideStatusText(StringConstantsMixin.strings.TRACK_DETAIL.UNHIDE);
      } else {
        setHideStatusText(StringConstantsMixin.strings.TRACK_DETAIL.HIDE);
      }
    }
  }, [isMobileWidth, context, data]);

  const photosUpload = photos => {
    if (handlePhotosUpload) {
      handlePhotosUpload(photos);
    }
  };

  const handleEditDescription = e => {
    setDescriptionValue(e.target.value);
  };

  const submitError = () => {
    setSubmitFailed(true);
    setSubmitInProgress(false);
  };

  const submitSuccess = info => {
    setIsEditShown(false);
    setSubmitInProgress(false);
    handleMapInfoChanged(info);
  };

  const handleDescriptionSubmit = newDescription => {
    const updateMapUrl = `/api/alltrails/maps/${data.id}?detail=deep&key=${ServerCommunicationUtil.apiKey}`;
    ServerCommunicationUtil.putApiEndpoint(updateMapUrl, { map: { description: newDescription } }, submitSuccess, submitError, () => {});
  };

  const handleAddAsTrailClick = e => {
    e.preventDefault();
    e.stopPropagation();
    window.open(LanguageSupportUtil.wrapUrlSafe(`/trail/new?recording=${data?.id}`, context?.languageRegionCode), '_blank');
  };

  const handleHideShowTrackClick = e => {
    e.preventDefault();
    e.stopPropagation();
    const url = `/api/alltrails/maps/${data?.id}/metadata`;
    setIsHidden(!hidden);
    if (hidden) {
      setHideStatusText('Hiding');
    } else {
      setHideStatusText('Unhiding');
    }
    ServerCommunicationUtil.postApiEndpoint(
      url,
      { hidden },
      () => {
        setIsHidden(hidden);
        if (hidden) {
          setHideStatusText(StringConstantsMixin.strings.TRACK_DETAIL.UNHIDE);
        } else {
          setHideStatusText(StringConstantsMixin.strings.TRACK_DETAIL.HIDE);
        }
      },
      () => {
        setHideStatusText(StringConstantsMixin.strings.FAILED);
      },
      () => {}
    );
  };

  const getSource = () => {
    if (!data || !data?.metadata || !data?.metadata.source) {
      return '?';
    }
    switch (data?.metadata.source) {
      case 1000:
        return 'iOS';
      case 1001:
        return 'Android';
      case 1043:
        return 'Garmin';
      case 1033:
        return 'Web';
      default:
        return 'Other';
    }
  };

  const toggleAccordionState = accordionName => {
    setAccordionStates({ ...accordionStates, [accordionName]: !accordionStates[accordionName] });
  };

  const saveEdit = () => {
    handleDescriptionSubmit(descriptionValue);
    setSubmitInProgress(true);
    setSubmitFailed(false);
  };

  const show = () => {
    if (userCanEdit(context, data?.user, true)) setIsEditShown(true);
  };

  const hide = () => {
    setIsEditShown(false);
  };

  const renderDescription = () => {
    const isEmptyMemberSection = isAnotherMemberView(context?.currentUser, data?.user) && (!data?.description_source || !data?.description);

    if (!data?.description && !isEmptyMemberSection) {
      return (
        <Accordion
          onClick={() => toggleAccordionState('description')}
          isOpen={accordionStates?.description}
          title={StringConstantsMixin.strings.TAB_NAMES.DESCRIPTION}
        >
          <DescriptionSection
            canEdit={userCanEdit(context, data?.user, true)}
            data={data?.description}
            dataSource={data?.description_source}
            handleEditDescription={handleEditDescription}
            placeholderText={StringConstantsMixin.strings.TRACK_DETAIL.TELL_WHAT_YOU_DID}
            isEditShown={isEditShown}
            submitInProgress={submitInProgress}
            submitFailed={submitFailed}
            hide={hide}
            show={show}
            handleSubmit={saveEdit}
            descriptionValue={descriptionValue}
          />
        </Accordion>
      );
    }
    return null;
  };

  const renderWaypointsSection = () => {
    const isEmptyMemberSection = isAnotherMemberView(context?.currentUser, data?.user) && data?.waypoints?.length === 0;
    const canEdit = userCanEdit(context, data?.user, true);
    if (!isEmptyMemberSection || context?.adminUser) {
      return (
        <Accordion
          onClick={() => toggleAccordionState('waypoints')}
          isOpen={accordionStates?.waypoints}
          title={StringConstantsMixin.strings.TAB_NAMES.WAYPOINTS}
        >
          <WaypointsWrapper
            canEdit={canEdit}
            waypoints={data?.waypoints}
            mapId={result.ID}
            messagingChannel={messagingChannel}
            isAdmin={context?.currentUser && context?.adminUser}
            className={styles.trackWaypointsContainer}
          />
        </Accordion>
      );
    }
    return null;
  };

  const renderPhotosTab = () => {
    const isEmptyMemberSection = isAnotherMemberView(context?.currentUser, data?.user) && data?.mapPhotos?.length === 0;
    if (!isEmptyMemberSection || context?.adminUser) {
      return (
        <Accordion
          onClick={() => toggleAccordionState('photos')}
          isOpen={accordionStates?.photos}
          title={StringConstantsMixin.strings.TAB_NAMES.PHOTOS}
        >
          <PhotosSection
            openTrailFormModal={openTrailFormModal}
            track={data}
            data={data?.mapPhotos}
            handlePhotosUpload={photosUpload}
            messagingChannel={messagingChannel}
            currentUser={context?.currentUser}
          />
        </Accordion>
      );
    }
    return null;
  };

  const style = { display: 'block', height: '100%', bottom: 0, left: 0 };
  const showSource = context?.currentUser && context?.adminUser && getSource();
  return (
    <div id="fullscreen-track-details" style={style}>
      <div className={styles.trackContainer}>
        <TrackDetailsCard
          context={context}
          forcePhotoId={resultCardFunctions.forcePhotoId(result.type, result.ID)}
          handleFavoriteClick={() => listMethods.handleFavoriteClick(result.type, result.ID)}
          height={180}
          isFavorite={listMethods.isFavorite(result.type, result.ID)}
          isMobileWidth={isMobileWidth}
          key={`track-${result.ID}`}
          linkable={false}
          messagingChannel={messagingChannel}
          showFavoriteHeart
          showStaticMap={false}
          tabletBrowser={context?.tabletBrowser}
          track={result}
          handleTrackUpdate={handleTrackUpdate}
          width={396}
          data={data}
          handleShareClick={handleShareClick}
          saveMapAsClone={saveMapAsClone}
          blockNonUserCreation={blockNonUserCreation}
        />
        <div className={styles.innerTrackDetails}>
          <Accordion
            onClick={() => toggleAccordionState('stats')}
            isOpen={accordionStates?.stats}
            title={StringConstantsMixin.strings.STATS.NORMAL.STATS}
          >
            <StatsTab
              data={data}
              currentPage={currentPage}
              displayMetric={context?.displayMetric}
              currentUser={context?.currentUser}
              languageRegionCode={context?.languageRegionCode}
              isMobileWidth={isMobileWidth}
              showMapMobile={showMapMobile}
            />
          </Accordion>
          {data?.splits && (
            <Accordion
              onClick={() => toggleAccordionState('splits')}
              isOpen={accordionStates?.splits}
              title={`${StringConstantsMixin.strings.STATS.NORMAL.SPLITS} ${showSource ? ` - ${getSource()}` : ''}`}
            >
              <SplitsTab splits={data?.splits} displayMetric={context?.displayMetric} currentUser={context?.currentUser} />
            </Accordion>
          )}
          {renderDescription()}
          {renderWaypointsSection()}
          {renderPhotosTab()}
          <LinkedCard
            trackTrail={trackTrail}
            context={context}
            data={data}
            hideText={hideStatusText}
            handleAddAsTrailClick={handleAddAsTrailClick}
            handleHideShowTrackClick={handleHideShowTrackClick}
            resultCardFunctions={resultCardFunctions}
            listMethods={listMethods}
            isMobileWidth={isMobileWidth}
            messagingChannel={messagingChannel}
          />
        </div>
      </div>
    </div>
  );
};

TrackPanel.propTypes = {
  context: object.isRequired,
  data: object.isRequired,
  isMobileWidth: bool,
  handlePhotosUpload: func,
  handleMapInfoChanged: func,
  messagingChannel: object,
  result: object,
  openTrailFormModal: func,
  resultCardFunctions: object,
  listMethods: object,
  handleTrackUpdate: func,
  handleShareClick: func,
  saveMapAsClone: func,
  blockNonUserCreation: func,
  currentPage: object,
  showMapMobile: func,
  trackTrail: object
};

TrackPanel.defaultProps = {
  isMobileWidth: false,
  handlePhotosUpload: () => {},
  handleMapInfoChanged: () => {},
  messagingChannel: {},
  result: {},
  openTrailFormModal: () => {},
  resultCardFunctions: {},
  listMethods: {},
  handleTrackUpdate: () => {},
  handleShareClick: () => {},
  saveMapAsClone: () => {},
  blockNonUserCreation: () => {},
  currentPage: {},
  showMapMobile: () => {},
  trackTrail: {}
};

export default TrackPanel;
